import React, { useState, useMemo } from "react";
import CustomSelect from "./CustomSelect.js";
import ExpandedContainer from "./ExpandedContainer.js";
import "./safari.css";

/**
 * Универсальный компонент таблицы данных (Grid-версия для Safari)
 */
function ClientTable({
  data = [],
  columns = [],
  options = {},
  onRowClick,
  loading = false,
  onCellClick,
  needExpandedRows = false,
  expandedCallback,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredId, setHoveredId] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "descending",
  });

  const {
    sortable = true,
    responsive = false,
    emptyText = "Нет данных для отображения",
    className = "",
  } = options;

  const handleSort = (key) => {
    if (!sortable) return;

    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      direction = "";
    }
    setSortConfig({ key, direction });
  };

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => {
      if (acc == null) return null;
      return acc[part];
    }, obj);
  };

  const sortedData = useMemo(() => {
    if (!sortable || !sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === "ascending" ? -1 : 1;
      if (bValue == null) return sortConfig.direction === "ascending" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "ascending"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortConfig.direction === "ascending") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else if (sortConfig.direction === "descending") {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
      return 0;
    });
  }, [data, sortConfig, sortable]);

  const formatCellValue = (value, column, item) => {
    if (value == null) return column.emptyValue || "—";

    if (column.format === "date" && value) {
      return new Date(value).toLocaleDateString();
    }

    if (column.format === "datetime" && value) {
      return new Date(value).toLocaleString();
    }

    if (column.format === "number" && typeof value === "number") {
      return new Intl.NumberFormat("ru-RU").format(value);
    }

    if (column.format === "enum" && typeof value === "number") {
      return (
        <CustomSelect
          value={value}
          classNamePostfix={'safari'}
          options={column.enum}
          onChange={(newValue) =>
            column.onCellClick && column.onCellClick(newValue, item)
          }
          item={item}
          isHovered={isHovered && hoveredId === item.id}
          onHover={(id) => {
            if (id) {
              setHoveredId(id);
              setIsHovered(true);
            } else {
              setHoveredId("");
              setIsHovered(false);
            }
          }}
        />
      );
    }

    if (column.format === "phone") {
      const numbers = value.replaceAll(/\D/gi, "");

      if (numbers.length < 10) {
        return numbers;
      }
      let res;
      if (numbers.length === 11) {
        res = numbers.slice(1);
      } else {
        if (+numbers[0] !== 9) return numbers;
        res = numbers;
      }
      return `+7 ${res.slice(0, 3)} ${res.slice(3, 6)} ${res.slice(
        6,
        8
      )} ${res.slice(8)}`;
    }

    if (typeof column.format === "function") {
      return column.format(value);
    }

    return typeof value === "string" && value.length
      ? `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`
      : value;
  };

  const renderCellContent = (item, column, rowIndex) => {
    const value = getNestedValue(item, column.key);

    if (column.renderCell) {
      const customRender = column.renderCell(item, column.key, value, rowIndex);
      if (customRender !== undefined) return customRender;
    }

    if (column.render) {
      return column.render(item, rowIndex);
    }

    return formatCellValue(value, column, item);
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return " ▷";

    if (sortConfig.direction === "ascending") {
      return " ▽";
    } else if (sortConfig.direction === "descending") {
      return " △";
    }
    return " ▷";
  };

  if (loading) {
    return (
      <div className={`data-table-loading ${className}`}>
        <div className="loading-spinner"></div>
        <div>Загрузка данных...</div>
      </div>
    );
  }

  if (!data.length) {
    return <div className={`data-table-empty ${className}`}>{emptyText}</div>;
  }

  return (
    <div
      className={`data-table-wrapper-safari ${
        responsive ? "responsive" : ""
      } ${className}`}
    >
      <div className="data-table-scroll">
        <table className="data-table-grid-header-safari">
          <thead>
            <tr style={{ height: "45px" }}>
              {columns.map((column, index) => (
                <th
                  key={column.key || index}
                  className={`data-table-grid-th-safari
    ${column.className || ""}
    ${sortable && column.sortable !== false ? "sortable" : ""}
    data-table-th
  `}
                  style={
                    column.width
                      ? {
                          width: column.width.strict || "max-content",
                          minWidth: column.width.min || column.width.strict,
                          maxWidth:
                            column.width.max ||
                            column.width.strict ||
                            "max-content",
                          textAlign: column.align ? column.align : "left",
                        }
                      : { textAlign: column.align ? column.align : "left" }
                  }
                  onClick={() =>
                    sortable &&
                    column.sortable !== false &&
                    handleSort(column.key)
                  }
                >
                  <div className="header-content">
                    <span>{column.title}</span>
                    {sortable &&
                      column.sortable !== false &&
                      renderSortIndicator(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, rowIndex) => (
              <React.Fragment key={item.id || rowIndex}>
                <tr
                  onClick={() => onRowClick && onRowClick(item, rowIndex)}
                  style={onRowClick ? { cursor: "pointer" } : {}}
                  className={`data-table-grid-row-safari ${
                    item.hasOwnProperty("ts") && "data-table-tr_resp"
                  }`}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={column.key || colIndex}
                      className={`data-table-grid-cell-safari
                      ${column.className || ""}
                      ${column.bodyClassName || ""}
                      ${column.align ? `text-${column.align}` : ""}
                      data-table-
                      ${!needExpandedRows ? "data-table-line" : ""}
                    `}
                      onClick={() => onCellClick && onCellClick(item)}
                    >
                      {renderCellContent(item, column, rowIndex)}
                    </td>
                  ))}
                </tr>
                {needExpandedRows && (
                  <tr className="expanded-row data-table-line">
                    <td colSpan={columns.length}>
                      <ExpandedContainer
                        item={item}
                        expandedCallback={expandedCallback}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ClientTable;
